import { apiFetch, API_BASE_URL, buildHeaders } from './config';
import { POLICY_FALLBACKS } from '../content/policyFallbacks';

// ==================== TypeScript Types ====================

export interface ComplianceRequirement {
  id: string;
  slug: string;
  display_name: string;
  version: string;
  is_mandatory: boolean;
  content_url: string;
  updated_at?: string;
}

export interface ComplianceStatus {
  compliance_id: string;
  slug: string;
  display_name: string;
  current_version: string;
  is_mandatory: boolean;
  content_url: string;
  accepted: boolean;
  accepted_version?: string;
  accepted_at?: string;
  needs_update: boolean;
}

export interface ComplianceUserStatus {
  statuses: ComplianceStatus[];
  all_accepted: boolean;
}

export interface ComplianceSubmitResponse {
  success: boolean;
  consent_id: string;
  slug: string;
  version: string;
  consented_at: string;
}

export interface ComplianceRequirementsResponse {
  requirements: ComplianceRequirement[];
}

export interface ComplianceVersionItem {
  version: string;
  effective_at: string;
}

export interface ComplianceMeta {
  slug: string;
  display_name: string;
  version: string;
  effective_at: string;
  hash: string;
  content_url: string;
  versions: ComplianceVersionItem[];
}

// ==================== API Functions ====================

/**
 * Fetch all mandatory compliance documents.
 * No authentication required.
 * 
 * @returns {Promise<ComplianceRequirementsResponse>} List of compliance requirements
 */
export async function getComplianceRequirements(): Promise<ComplianceRequirementsResponse> {
  return apiFetch<ComplianceRequirementsResponse>('compliance/requirements');
}

/**
 * Check the authenticated user's consent status for all compliance requirements.
 * Requires authentication.
 * 
 * @returns {Promise<ComplianceUserStatus>} User's compliance status
 */
export async function getUserComplianceStatus(): Promise<ComplianceUserStatus> {
  return apiFetch<ComplianceUserStatus>('compliance/user-status');
}

/**
 * Submit user's consent to a specific compliance document.
 * Requires authentication.
 * Records IP address, user agent, and timestamp automatically.
 * 
 * @param {string} complianceId - UUID of the compliance definition
 * @returns {Promise<ComplianceSubmitResponse>} Confirmation of consent submission
 */
export async function submitCompliance(complianceId: string): Promise<ComplianceSubmitResponse> {
  return apiFetch<ComplianceSubmitResponse>('compliance/submit', {
    method: 'POST',
    body: JSON.stringify({ compliance_id: complianceId }),
  });
}

// ==================== Compliance content ====================
//
// Three layers, simplest-first, so a policy page ALWAYS renders text and never
// shows an error or a blank page:
//   1. POLICY_FALLBACKS  — bundled in the app build; works even on a first
//      visit with no cache and an unreachable backend.
//   2. localStorage      — the last good copy fetched on this device.
//   3. live fetch        — overrides both when the backend is reachable.

const TEXT_CACHE_PREFIX = 'compliance_text:';
const HASH_CACHE_PREFIX = 'compliance_hash:';

function readCachedText(slug: string): string {
  if (typeof localStorage === 'undefined') return '';
  try {
    return localStorage.getItem(TEXT_CACHE_PREFIX + slug) ?? '';
  } catch {
    return '';
  }
}

function readCachedHash(slug: string): string {
  if (typeof localStorage === 'undefined') return '';
  try {
    return localStorage.getItem(HASH_CACHE_PREFIX + slug) ?? '';
  } catch {
    return '';
  }
}

function writeCachedText(slug: string, content: string, hash?: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(TEXT_CACHE_PREFIX + slug, content);
    if (hash) localStorage.setItem(HASH_CACHE_PREFIX + slug, hash);
  } catch {
    /* quota / serialization errors are non-fatal */
  }
}

/**
 * Fetch a policy's metadata (current version, effective date, content hash, and
 * full version history). Returns null on any failure so callers can fall back to
 * cached/bundled content. No authentication required.
 */
export async function getComplianceMeta(slug: string): Promise<ComplianceMeta | null> {
  try {
    const res = await fetch(`${API_BASE_URL}compliance/content/${slug}/meta`, {
      headers: buildHeaders(),
      credentials: 'include',
    });
    if (res.ok) return (await res.json()) as ComplianceMeta;
  } catch {
    /* offline / error — caller uses cache + fallback */
  }
  return null;
}

/**
 * Synchronously return the best content we already have for a slug — the last
 * good copy cached on this device, or the bundled fallback. Always non-empty
 * for a known policy slug, so the UI can render instantly with no spinner and
 * no risk of a blank page.
 */
export function getInitialComplianceContent(slug: string): string {
  return readCachedText(slug) || (POLICY_FALLBACKS[slug] ?? '');
}

/**
 * Fetch the policy markdown for a slug, downloading the file ONLY when it has
 * actually changed. Workflow: the caller passes the current `expectedHash` (from
 * getComplianceMeta); if it matches the hash we already cached, the cached copy
 * is returned with no download. Otherwise the file is fetched and stored
 * (content + hash) as the device cache. Never throws and never returns empty:
 * on any failure it falls back to the last cached copy, then the bundled copy.
 *
 * @param {string} slug - Document slug (e.g., 'privacy-policy', 'terms-of-service')
 * @param {string} [expectedHash] - The current content hash from the meta endpoint.
 * @returns {Promise<string>} Markdown content of the document
 */
export async function getComplianceContent(slug: string, expectedHash?: string): Promise<string> {
  const cachedText = readCachedText(slug);

  // Hash matches what we already have → serve from cache, no download.
  if (expectedHash && cachedText && readCachedHash(slug) === expectedHash) {
    return cachedText;
  }

  try {
    const res = await fetch(`${API_BASE_URL}compliance/content/${slug}`, {
      headers: {
        ...buildHeaders(),
        // Lets the server answer 304 when unchanged (belt-and-braces with the
        // hash check above).
        ...(readCachedHash(slug) ? { 'If-None-Match': `"${readCachedHash(slug)}"` } : {}),
      },
      credentials: 'include',
    });
    if (res.status === 304 && cachedText) {
      return cachedText;
    }
    if (res.ok) {
      const text = await res.text();
      if (text.trim()) {
        const etag = (res.headers.get('ETag') ?? '').replace(/"/g, '');
        writeCachedText(slug, text, etag || expectedHash);
        return text;
      }
    }
  } catch {
    /* offline / network error — fall through to cached or bundled copy */
  }

  return getInitialComplianceContent(slug);
}

/**
 * Helper to get a specific compliance requirement by slug.
 * 
 * @param {string} slug - Document slug
 * @returns {Promise<ComplianceRequirement | undefined>} The requirement or undefined if not found
 */
export async function getComplianceRequirementBySlug(
  slug: string
): Promise<ComplianceRequirement | undefined> {
  const { requirements } = await getComplianceRequirements();
  return requirements.find((r) => r.slug === slug);
}

/**
 * Helper to check if user needs to accept any compliance documents.
 * 
 * @returns {Promise<ComplianceStatus[]>} List of compliance documents that need user action
 */
export async function getOutdatedCompliances(): Promise<ComplianceStatus[]> {
  const status = await getUserComplianceStatus();
  return status.statuses.filter(
    (s) => s.is_mandatory && (!s.accepted || s.needs_update) && s.slug !== 'photo-consent'
  );
}
